import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import {
  ArrowLeft,
  Wallet,
  CreditCard,
  Clock,
  CheckCircle,
  AlertCircle,
  ExternalLink,
  RefreshCw,
  PoundSterling,
  Calendar,
  TrendingUp,
  Loader2,
  Receipt,
  ArrowRight,
  Save
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const COMPANY_LOGO = "https://customer-assets.emergentagent.com/job_bizcommerce-19/artifacts/7hv4d1j9_LOGO%20PNG.png";

const DAYS_OF_WEEK = [
  { value: "0", label: "Monday" },
  { value: "1", label: "Tuesday" },
  { value: "2", label: "Wednesday" },
  { value: "3", label: "Thursday" },
  { value: "4", label: "Friday" },
  { value: "5", label: "Saturday" },
  { value: "6", label: "Sunday" }
];

const DAYS_OF_MONTH = Array.from({ length: 28 }, (_, i) => ({
  value: String(i + 1),
  label: `${i + 1}${i === 0 ? 'st' : i === 1 ? 'nd' : i === 2 ? 'rd' : 'th'}`
}));

const MerchantPayoutsPage = () => {
  const { user, token } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [connectStatus, setConnectStatus] = useState(null);
  const [balance, setBalance] = useState({ available: 0, pending: 0 });
  const [payouts, setPayouts] = useState([]);
  const [payoutPreview, setPayoutPreview] = useState(null);
  const [onboarding, setOnboarding] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [showSyncInput, setShowSyncInput] = useState(false);
  const [syncAccountId, setSyncAccountId] = useState("");
  
  // Payout schedule state
  const [frequency, setFrequency] = useState("weekly");
  const [dayOfWeek, setDayOfWeek] = useState("4"); // Friday
  const [dayOfMonth, setDayOfMonth] = useState("1");
  const [savingSchedule, setSavingSchedule] = useState(false);

  useEffect(() => {
    if (searchParams.get("success") === "true") {
      toast.success("Stripe Connect setup completed!");
    } else if (searchParams.get("refresh") === "true") {
      toast.info("Please complete your Stripe setup");
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    const headers = { Authorization: `Bearer ${token}` };

    try {
      const [statusRes, balanceRes, payoutsRes, previewRes] = await Promise.all([
        fetch(`${API}/merchant/connect/status`, { headers, credentials: "include" }),
        fetch(`${API}/merchant/connect/balance`, { headers, credentials: "include" }).catch(() => null),
        fetch(`${API}/merchant/payout-history`, { headers, credentials: "include" }).catch(() => null),
        fetch(`${API}/merchant/payout-preview`, { headers, credentials: "include" }).catch(() => null)
      ]);

      if (statusRes.ok) {
        const status = await statusRes.json();
        setConnectStatus(status);
      }

      if (balanceRes?.ok) {
        setBalance(await balanceRes.json());
      }

      if (payoutsRes?.ok) {
        const data = await payoutsRes.json();
        setPayouts(data.payouts || []);
      }

      if (previewRes?.ok) {
        const preview = await previewRes.json();
        setPayoutPreview(preview);
        
        // Set schedule from config
        if (preview.payout_config) {
          setFrequency(preview.payout_config.frequency || "weekly");
          if (preview.payout_config.day_of_week !== null) {
            setDayOfWeek(String(preview.payout_config.day_of_week));
          }
          if (preview.payout_config.day_of_month !== null) {
            setDayOfMonth(String(preview.payout_config.day_of_month));
          }
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartOnboarding = async () => {
    setOnboarding(true);
    try {
      console.log("[CONNECT] Starting onboarding...");
      const response = await fetch(`${API}/merchant/connect/onboard`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        credentials: "include"
      });

      console.log("[CONNECT] Response status:", response.status);
      
      // Read response as text first to avoid body stream issues
      let responseText = "";
      try {
        responseText = await response.text();
        console.log("[CONNECT] Response text:", responseText);
      } catch (readErr) {
        console.error("[CONNECT] Failed to read response:", readErr);
        toast.error("Failed to read server response. Please try again.");
        return;
      }
      
      // Parse JSON
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseErr) {
        console.error("[CONNECT] Failed to parse JSON:", responseText);
        toast.error(`Server error: ${responseText.substring(0, 200)}`);
        return;
      }
      
      if (response.ok && data.url) {
        console.log("[CONNECT] Success! Redirecting to:", data.url);
        toast.success("Redirecting to Stripe...");
        window.location.href = data.url;
      } else {
        // Show detailed error from backend
        const errorMsg = data.detail || data.error || data.message || "Failed to start onboarding";
        const hint = data.hint || "";
        console.error("[CONNECT] Error:", errorMsg, hint);
        toast.error(`${errorMsg}${hint ? ` (${hint})` : ""}`, { duration: 15000 });
      }
    } catch (error) {
      console.error("[CONNECT] Network error:", error);
      toast.error(`Network error: ${error.message}. Please check your internet connection.`);
    } finally {
      setOnboarding(false);
    }
  };

  const handleSyncAccount = async () => {
    setSyncing(true);
    try {
      const response = await fetch(`${API}/merchant/connect/sync`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ account_id: syncAccountId || null })
      });

      // Read response as text first (safest approach to avoid body stream issues)
      let responseText = "";
      try {
        responseText = await response.text();
      } catch (readErr) {
        console.error("Failed to read response:", readErr);
        toast.error("Failed to read server response. Please try again.");
        return;
      }
      
      // Parse the JSON from text
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseErr) {
        console.error("Failed to parse response:", responseText);
        if (responseText && responseText.length < 500) {
          toast.error(`Server error: ${responseText}`);
        } else {
          toast.error("Server returned invalid response. Please try again.");
        }
        return;
      }
      
      if (response.ok && data.success) {
        toast.success(data.message || "Account synced successfully!");
        setShowSyncInput(false);
        setSyncAccountId("");
        fetchData(); // Refresh status
      } else {
        // Show detailed error from backend
        const errorMsg = data.detail || data.error || data.message || "Failed to sync account";
        toast.error(errorMsg, { duration: 8000 });
        console.error("Sync Error:", data);
      }
    } catch (error) {
      console.error("Sync Exception:", error);
      toast.error(`Network error: ${error.message}. Please try again.`);
    } finally {
      setSyncing(false);
    }
  };

  const handleForceClear = async () => {
    if (!confirm("This will completely reset your Stripe Connect. You'll need to reconnect from scratch. Continue?")) {
      return;
    }
    setSyncing(true);
    try {
      const response = await fetch(`${API}/merchant/connect/force-clear`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        credentials: "include"
      });

      if (response.ok) {
        toast.success("Stripe Connect reset. Click 'Connect with Stripe' to start fresh.");
        setConnectStatus(null);
        fetchData();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to reset");
      }
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    } finally {
      setSyncing(false);
    }
  };

  const handleSaveSchedule = async () => {
    setSavingSchedule(true);
    try {
      const response = await fetch(`${API}/merchant/payout-config`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          frequency,
          day_of_week: frequency === "weekly" ? parseInt(dayOfWeek) : null,
          day_of_month: frequency === "monthly" ? parseInt(dayOfMonth) : null
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Schedule saved! Next payout: ${data.next_payout.formatted}`);
        fetchData(); // Refresh preview
      } else {
        toast.error("Failed to save schedule");
      }
    } catch (error) {
      toast.error("Failed to save schedule");
    } finally {
      setSavingSchedule(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "completed":
      case "paid":
        return <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs rounded-full">Paid</span>;
      case "pending":
        return <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded-full">Pending</span>;
      case "in_transit":
        return <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">In Transit</span>;
      case "failed":
        return <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">Failed</span>;
      default:
        return <span className="px-2 py-1 bg-slate-100 text-slate-700 text-xs rounded-full">{status}</span>;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/merchant" className="text-slate-500 hover:text-slate-700">
                <ArrowLeft className="h-5 w-5" />
              </Link>
              <img src={COMPANY_LOGO} alt="V" className="h-8 w-auto" />
              <span className="font-heading font-bold text-slate-900">Payouts</span>
            </div>
            <Button variant="outline" size="sm" onClick={fetchData} className="rounded-full">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-12 h-12 text-indigo-500 animate-spin" />
          </div>
        ) : !connectStatus?.connected || connectStatus?.status === "not_started" ? (
          /* Onboarding Card */
          <div className="max-w-lg mx-auto">
            <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center">
              <div className="w-20 h-20 rounded-full bg-indigo-100 flex items-center justify-center mx-auto mb-6">
                <Wallet className="h-10 w-10 text-indigo-600" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-slate-900 mb-2">Set Up Payouts</h1>
              <p className="text-slate-500 mb-6">
                Connect your bank account to receive payments from your sales. We use Stripe for secure, fast payouts.
              </p>

              <div className="bg-slate-50 rounded-xl p-4 mb-6 text-left">
                <h3 className="font-semibold text-slate-900 mb-3">How it works:</h3>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                    Customer buys your product
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                    We take 2% platform fee
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                    98% goes directly to your bank
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                    Choose daily, weekly, or monthly payouts
                  </li>
                </ul>
              </div>

              <Button
                onClick={handleStartOnboarding}
                disabled={onboarding}
                className="w-full h-12 bg-indigo-600 hover:bg-indigo-700 rounded-full"
                data-testid="connect-stripe-btn"
              >
                {onboarding ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Connecting...
                  </div>
                ) : (
                  <>
                    <CreditCard className="h-5 w-5 mr-2" />
                    Connect with Stripe
                  </>
                )}
              </Button>

              {/* Sync existing account option */}
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-xs text-gray-500 mb-2 text-center">
                  Already completed Stripe Connect setup but it's not showing?
                </p>
                {!showSyncInput ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowSyncInput(true)}
                    className="w-full text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
                  >
                    <RefreshCw className="h-4 w-4 mr-1" />
                    Sync Existing Account
                  </Button>
                ) : (
                  <div className="space-y-2">
                    <input
                      type="text"
                      placeholder="Stripe Account ID (acct_...)"
                      value={syncAccountId}
                      onChange={(e) => setSyncAccountId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                    />
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { setShowSyncInput(false); setSyncAccountId(""); }}
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleSyncAccount}
                        disabled={syncing}
                        className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                      >
                        {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sync"}
                      </Button>
                    </div>
                    <p className="text-xs text-gray-400 text-center">
                      Leave blank to auto-detect by email
                    </p>
                  </div>
                )}
                
                {/* Force Reset Option */}
                <div className="pt-4 mt-4 border-t border-gray-100">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleForceClear}
                    disabled={syncing}
                    className="w-full text-red-500 hover:text-red-600 hover:bg-red-50 text-xs"
                  >
                    🔄 Having issues? Force Reset & Start Fresh
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ) : connectStatus?.status === "pending" ? (
          /* Pending Setup */
          <div className="max-w-lg mx-auto">
            <div className="bg-white rounded-2xl border border-amber-200 p-8 text-center">
              <div className="w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-6">
                <AlertCircle className="h-10 w-10 text-amber-600" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-slate-900 mb-2">Complete Your Setup</h1>
              <p className="text-slate-500 mb-6">
                Your Stripe account is not fully verified yet. Please complete the verification to receive payouts.
              </p>
              <Button
                onClick={handleStartOnboarding}
                disabled={onboarding}
                className="w-full h-12 bg-amber-600 hover:bg-amber-700 rounded-full"
              >
                {onboarding ? "Loading..." : "Complete Verification"}
                <ExternalLink className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </div>
        ) : (
          /* Active Dashboard */
          <div className="space-y-6">
            {/* Earnings Preview Cards */}
            <div className="grid sm:grid-cols-4 gap-4">
              <div className="bg-gradient-to-br from-indigo-600 to-violet-600 rounded-xl p-5 text-white">
                <p className="text-indigo-100 text-sm mb-1">Pending Payout</p>
                <p className="text-3xl font-heading font-bold">
                  £{payoutPreview?.pending_payout?.amount?.toFixed(2) || "0.00"}
                </p>
                <p className="text-indigo-200 text-xs mt-1">
                  {payoutPreview?.pending_payout?.days_until || 0} days
                </p>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <p className="text-slate-500 text-sm mb-1">This Week</p>
                <p className="text-2xl font-heading font-bold text-slate-900">
                  £{payoutPreview?.this_week?.toFixed(2) || "0.00"}
                </p>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <p className="text-slate-500 text-sm mb-1">This Month</p>
                <p className="text-2xl font-heading font-bold text-slate-900">
                  £{payoutPreview?.this_month?.toFixed(2) || "0.00"}
                </p>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <p className="text-slate-500 text-sm mb-1">Lifetime</p>
                <p className="text-2xl font-heading font-bold text-emerald-600">
                  £{payoutPreview?.lifetime?.toFixed(2) || "0.00"}
                </p>
              </div>
            </div>

            {/* Earnings Breakdown */}
            {payoutPreview?.breakdown && Object.keys(payoutPreview.breakdown).length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h2 className="font-heading font-semibold text-lg text-slate-900 mb-4">
                  Earnings Breakdown (This Month)
                </h2>
                <div className="space-y-3">
                  {payoutPreview.breakdown.orders && (
                    <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                          <PoundSterling className="h-4 w-4 text-emerald-600" />
                        </div>
                        <span className="text-slate-700">Orders</span>
                      </div>
                      <div className="text-right">
                        <span className="font-semibold text-slate-900">
                          £{payoutPreview.breakdown.orders.amount?.toFixed(2)}
                        </span>
                        <span className="text-slate-500 text-sm ml-2">
                          ({payoutPreview.breakdown.orders.percent}%)
                        </span>
                      </div>
                    </div>
                  )}
                  {payoutPreview.breakdown.subscriptions?.amount > 0 && (
                    <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                          <RefreshCw className="h-4 w-4 text-blue-600" />
                        </div>
                        <span className="text-slate-700">Subscriptions</span>
                      </div>
                      <div className="text-right">
                        <span className="font-semibold text-slate-900">
                          £{payoutPreview.breakdown.subscriptions.amount?.toFixed(2)}
                        </span>
                        <span className="text-slate-500 text-sm ml-2">
                          ({payoutPreview.breakdown.subscriptions.percent}%)
                        </span>
                      </div>
                    </div>
                  )}
                  {payoutPreview.breakdown.addons?.amount > 0 && (
                    <div className="flex items-center justify-between p-3 bg-violet-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center">
                          <TrendingUp className="h-4 w-4 text-violet-600" />
                        </div>
                        <span className="text-slate-700">Add-ons</span>
                      </div>
                      <div className="text-right">
                        <span className="font-semibold text-slate-900">
                          £{payoutPreview.breakdown.addons.amount?.toFixed(2)}
                        </span>
                        <span className="text-slate-500 text-sm ml-2">
                          ({payoutPreview.breakdown.addons.percent}%)
                        </span>
                      </div>
                    </div>
                  )}
                  {payoutPreview.breakdown.platform_fee && (
                    <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                          <AlertCircle className="h-4 w-4 text-red-600" />
                        </div>
                        <span className="text-slate-700">Platform Fee</span>
                      </div>
                      <div className="text-right">
                        <span className="font-semibold text-red-600">
                          £{payoutPreview.breakdown.platform_fee.amount?.toFixed(2)}
                        </span>
                        <span className="text-slate-500 text-sm ml-2">
                          ({payoutPreview.breakdown.platform_fee.percent}%)
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Payout Schedule Selector */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="font-heading font-semibold text-lg text-slate-900 mb-4">
                Payout Schedule
              </h2>
              
              <div className="grid sm:grid-cols-4 gap-4 mb-6">
                {/* Frequency */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Frequency</label>
                  <Select value={frequency} onValueChange={setFrequency}>
                    <SelectTrigger className="w-full" data-testid="frequency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Day of Week (for weekly) */}
                {frequency === "weekly" && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Day of Week</label>
                    <Select value={dayOfWeek} onValueChange={setDayOfWeek}>
                      <SelectTrigger className="w-full" data-testid="day-of-week-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DAYS_OF_WEEK.map(day => (
                          <SelectItem key={day.value} value={day.value}>{day.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                {/* Day of Month (for monthly) */}
                {frequency === "monthly" && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Day of Month</label>
                    <Select value={dayOfMonth} onValueChange={setDayOfMonth}>
                      <SelectTrigger className="w-full" data-testid="day-of-month-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DAYS_OF_MONTH.map(day => (
                          <SelectItem key={day.value} value={day.value}>{day.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                {/* Save Button */}
                <div className="flex items-end">
                  <Button
                    onClick={handleSaveSchedule}
                    disabled={savingSchedule}
                    className="w-full bg-indigo-600 hover:bg-indigo-700 rounded-full"
                    data-testid="save-schedule-btn"
                  >
                    {savingSchedule ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Save Schedule
                      </>
                    )}
                  </Button>
                </div>
              </div>
              
              {/* Next Payout Preview */}
              {payoutPreview?.next_payout && (
                <div className="flex items-center justify-between p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl border border-emerald-200">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                      <Calendar className="h-5 w-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">Next Payout</p>
                      <p className="text-sm text-emerald-700">{payoutPreview.next_payout.formatted}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-heading font-bold text-emerald-600">
                      £{payoutPreview.next_payout.estimated_amount?.toFixed(2)}
                    </p>
                    <p className="text-xs text-slate-500">estimated</p>
                  </div>
                </div>
              )}
              
              {/* Last Payout */}
              {payoutPreview?.last_payout?.amount > 0 && (
                <div className="flex items-center justify-between p-4 mt-4 bg-slate-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                      <CheckCircle className="h-5 w-5 text-slate-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-700">Last Payout</p>
                      <p className="text-sm text-slate-500">
                        {payoutPreview.last_payout.date 
                          ? new Date(payoutPreview.last_payout.date).toLocaleDateString("en-GB", { day: "numeric", month: "short" })
                          : "N/A"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-xl font-bold text-slate-900">
                      £{payoutPreview.last_payout.amount?.toFixed(2)}
                    </span>
                    {payoutPreview.last_payout.receipt_url && (
                      <a
                        href={payoutPreview.last_payout.receipt_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-indigo-600 hover:text-indigo-700"
                      >
                        <Receipt className="h-5 w-5" />
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Payout History */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="font-heading font-semibold text-lg text-slate-900 mb-4">
                Payout History
              </h2>
              {payouts.length === 0 ? (
                <div className="text-center py-8">
                  <Wallet className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No payouts yet</p>
                  <p className="text-sm text-slate-400">Payouts will appear here once processed</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {payouts.map((payout, idx) => (
                    <div 
                      key={payout.id || idx} 
                      className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"
                      data-testid={`payout-row-${idx}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center">
                          <PoundSterling className="h-5 w-5 text-slate-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-slate-900">
                            £{payout.amount?.toFixed(2)}
                          </p>
                          <p className="text-sm text-slate-500">
                            {payout.date 
                              ? new Date(payout.date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
                              : payout.arrival_date 
                              ? new Date(payout.arrival_date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
                              : "Processing"}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {getStatusBadge(payout.status)}
                        {payout.receipt_url && (
                          <a
                            href={payout.receipt_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-full transition-colors"
                          >
                            <Receipt className="h-4 w-4" />
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Stripe Dashboard Link */}
            <div className="text-center">
              <a
                href="https://dashboard.stripe.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-700"
              >
                View full details in Stripe Dashboard
                <ExternalLink className="h-4 w-4" />
              </a>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default MerchantPayoutsPage;
